import { getServerApplicationContext } from '@hc-frontend/core-context';
import type {
  DomainIds,
  FeaturedAds,
  HealthCareIdentifier,
  Prospect,
} from '@hc-frontend/core-types';
import { AdServer } from '@hc-frontend/core-types';
import { getIpFromHeaders } from '@hc-frontend/core-utils-net';
import { randomUUID } from 'crypto';
import { userAgent as getUserAgent } from 'next/server';

import { buildQuinStreetRequest } from './build-quin-street-request';
import { fetchQuinStreetAdsInternal } from './fetch-quin-street-ads-internal';

type Props = {
  prospect: Prospect;
  identifier: HealthCareIdentifier;
  domainIdentifiers: Partial<DomainIds>;
  /** QuinStreet listings API URL (staging or prod). */
  urlAPI: string;
  /** QuinStreet SRC (`ni_ad_client`). */
  adClientId: string;
  /** QuinStreet `coverage_type`, e.g. `Medicare Advantage`. */
  coverageType: string;
  /** Experiment arm, sent as `ni_var2` so revenue splits by arm. */
  testArm?: string;
  /** Page placement, sent as `ni_var3`. */
  placement?: string;
  /** Abort the QuinStreet request after this many ms; no timeout when omitted. */
  timeoutMs?: number;
};

/** Landing page without its query string, which may carry tracking params. */
function toLandingPageUrl(raw: string | null): string | undefined {
  if (!raw) return undefined;

  try {
    const url = new URL(raw);
    return `${url.origin}${url.pathname}`;
  } catch {
    return undefined;
  }
}

/**
 * Fetches CPC listings from the QuinStreet (NextInsure) listings API for the
 * current request's user and maps them to `FeaturedAds`, in `rank` order.
 *
 * The returned `id` (our impression id) is the request's `ni_trn_id`, so our
 * impression and click events join QuinStreet's reporting. Never throws: any
 * failure yields no ads.
 */
export async function fetchQuinStreetAds({
  prospect,
  identifier,
  domainIdentifiers,
  urlAPI,
  adClientId,
  coverageType,
  testArm,
  placement,
  timeoutMs,
}: Props): Promise<FeaturedAds> {
  const transactionId = randomUUID();
  const headers = await (await import('next/headers')).headers();

  const body = buildQuinStreetRequest(prospect, {
    adClientId,
    coverageType,
    transactionId,
    ip: getIpFromHeaders(headers) ?? undefined,
    userAgent: getUserAgent({ headers }).ua,
    host: headers.get('x-forwarded-host') ?? headers.get('host') ?? undefined,
    landingPageUrl: toLandingPageUrl(headers.get('x-url')),
    sessionRef: identifier.sessionId,
    subIds: {
      var1: domainIdentifiers.sessionId,
      var2: testArm,
      var3: placement,
    },
  });

  // PHI audit trail: who, what and when — field names only, never values.
  getServerApplicationContext(fetchQuinStreetAds).log({
    type: 'info',
    context: 'server',
    message: 'quinStreetLeadDataShared',
    data: {
      partner: 'QuinStreet',
      action: 'shareLeadData',
      transactionId,
      sessionId: identifier.sessionId,
      userId: identifier.userId,
      fields: [
        ...Object.keys(body.contact).map((field) => `contact.${field}`),
        ...body.individuals.flatMap((individual) =>
          Object.keys(individual).map((field) => `individual.${field}`),
        ),
      ],
      timestamp: new Date().toISOString(),
    },
  });

  const ads = await fetchQuinStreetAdsInternal(urlAPI, body, { timeoutMs });

  return {
    id: transactionId,
    adsSource: AdServer.enum.NextInsure,
    searchMethod: undefined,
    segmentationFit: undefined,
    result: ads,
  };
}
